import React, { useEffect, useState } from "react";
import { Box, Flex, Heading, Image, Text } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import CreateUserAttempt from './CreateUserAttempt';
import HandIcon from "../Images/handIcon.svg";
import PerformanceCardDashboard from "./PerformanceCardDashboard";
import SubjectTopicTracker from "./SubjectTopicTracker";
import { BASE_API_URL } from './config';
import { a } from "./appColors";

const wave = keyframes`
  0% { transform: rotate(0deg); }
  15% { transform: rotate(14deg); }
  30% { transform: rotate(-8deg); }
  40% { transform: rotate(14deg); }
  50% { transform: rotate(-4deg); }
  60% { transform: rotate(10deg); }
  70%, 100% { transform: rotate(0deg); }
`;
const blink = keyframes`50% { opacity: 0; }`;

const timeGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
};

const DashboardMain = () => {
    const [userAttempts, setUserAttempts] = useState([]);
    const [userName, setUserName] = useState(null);
    const [loading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [greeting] = useState(timeGreeting);
    const [typed, setTyped] = useState("");
    const [doneTyping, setDoneTyping] = useState(false);


    useEffect(() => {
        const fetchUser = async () => {
            try {
                const accessToken = localStorage.getItem('accessToken');
                const tokenType = localStorage.getItem('tokenType');
                if (!accessToken || !tokenType) {
                    throw new Error('Authorization token not found');
                }
                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `${tokenType} ${accessToken}`,
                };

                const response = await fetch(`${BASE_API_URL}/api/v1/users/own`, {
                    method: 'GET',
                    headers: headers,
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch from the API');
                }
                const data = await response.json();
                setUserName(data.first_name);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUser();
    }, []);

    // Typewriter reveal: type out "<greeting>, <name>" once the name loads,
    // then reveal the waving hand.
    useEffect(() => {
        if (!userName) return;
        const full = `${greeting}, ${userName}`;
        setTyped("");
        setDoneTyping(false);
        let i = 0;
        const id = setInterval(() => {
            i += 1;
            setTyped(full.slice(0, i));
            if (i >= full.length) {
                clearInterval(id);
                setDoneTyping(true);
            }
        }, 55);
        return () => clearInterval(id);
    }, [userName, greeting]);

    useEffect(() => {
        const fetchUserAttempts = async () => {
            try {
                const accessToken = localStorage.getItem('accessToken');
                const tokenType = localStorage.getItem('tokenType');
                if (!accessToken || !tokenType) {
                    throw new Error('Authorization token not found');
                }
                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `${tokenType} ${accessToken}`,
                };

                const response = await fetch(`${BASE_API_URL}/api/v1/user-paper-attempts/detail/?limit=3`, {
                    method: 'GET',
                    headers: headers,
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch from the API');
                }
                const data = await response.json();
                setUserAttempts(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserAttempts();
    }, []);

    return (
        <Flex overflowX="hidden" >
            <Box

                width="100%"
            >

                <Box spacing={4}>
                    <Flex flexDirection="row" alignItems="center" gap={2} mb={8} minH="35px">
                        <Heading
                        color={a.text}
                        fontSize="28px"
                        fontWeight="500"
                    >
                        {typed}
                        {!doneTyping && (
                            <Box
                                as="span"
                                ml="2px"
                                fontWeight="400"
                                color={a.accent}
                                animation={`${blink} 1s step-end infinite`}
                            >
                                |
                            </Box>
                        )}
                    </Heading>
                        <Image
                            src={HandIcon}
                            alt="Waving hand"
                            boxSize="35px"
                            transformOrigin="70% 80%"
                            opacity={doneTyping ? 1 : 0}
                            transition="opacity 0.25s ease"
                            animation={doneTyping ? `${wave} 1.4s ease-in-out` : undefined}
                        />
                    </Flex>

                    <Flex
                        height="108px"
                        width="473px"
                        gap="103px"
                        flexDirection = {{ base: "column", lg: "row", sm: "column", md: "column"}}
                        mb = {{ base: "254px", lg: "24px"}}
                    >
                        <PerformanceCardDashboard />
                        
                    </Flex>

                    <Flex direction ={{ base: "column", md: "row" }} gap="32px" marginTop="44px">
                        {/* Recently accessed papers */}
                        <Box
                            w={{ base: "100%", md: "50%" }} // Adjust width to fit the available space
                            height="400px"
                            borderRadius="14px"
                            border="2px solid" borderColor={a.border}
                            padding="35px 0px 35.13px 18px"
                            alignItems="center"
                            overflowX="auto"
                            css={{
                                '&::-webkit-scrollbar': { width: '0px' },
                                '-ms-overflow-style': 'none',
                                'scrollbar-width': 'none',
                            }}
                            position="relative"
                        >   
                            <Box> <Heading
                               color={a.text} fontSize="20px" fontWeight="400"
                            > Your most recent papers </Heading>
                            </Box> 
                            <Flex
                                flexDirection="row"
                                gap="16px"
                                alignItems="center"
                                justifyContent="flex-start"
                                width = "max-content"
                                marginTop="44px"
                                onMouseDown={e => {
                                    const container = e.currentTarget;
                                    let isDown = false;
                                    let startX;
                                    let scrollLeft;

                                    container.addEventListener('mousedown', (e) => {
                                        isDown = true;
                                        container.classList.add('active');
                                        startX = e.pageX - container.offsetLeft;
                                        scrollLeft = container.scrollLeft;
                                    });

                                    container.addEventListener('mouseleave', () => {
                                        isDown = false;
                                        container.classList.remove('active');
                                    });

                                    container.addEventListener('mouseup', () => {
                                        isDown = false;
                                        container.classList.remove('active');
                                    });

                                    container.addEventListener('mousemove', (e) => {
                                        if (!isDown) return;
                                        e.preventDefault();
                                        const x = e.pageX - container.offsetLeft;
                                        const walk = (x - startX) * 3;
                                        container.scrollLeft = scrollLeft - walk;
                                    });
                                }}
                            >
                                {userAttempts.length > 0 ? (
                                    userAttempts.map((attempt, index) => (
                                        <CreateUserAttempt
                                            key={index}
                                            subjectName={`${attempt.subject_name} ${attempt.subject_code}`}
                                            paperName={`Paper ${attempt.paper_name.split(' ')[1]}${attempt.paper_variant} ${attempt.exam_series_month === "MAY_JUNE" ? "May/June" : attempt.exam_series_month} ${attempt.exam_series_year}`}
                                            numOfQuestionsLeft={attempt.questions_remaining}
                                            mark={attempt.total_scored_marks}
                                            totalMark={attempt.total_marks}
                                            inProgress={attempt.completion_status !== "COMPLETE"}
                                            progressPercentage={Math.round(attempt.percentage_complete)}
                                            user_paper_attempt_guid={attempt.guid}
                                            examBoardCode={attempt.exam_board_code}
                                            qualificationCode={attempt.exam_qualification_code}
                                        />
                                    ))
                                ) : (
                                    <Text marginTop="24px" color={a.textSub} fontSize="12px" fontWeight="200"> You have not attempted to solve any papers yet</Text>
                                )}
                            </Flex>
                        </Box>

                        {/* Subject Topic Tracker */}
                        <SubjectTopicTracker  />
                        </Flex>
                </Box>
            </Box>
        </Flex>
    );
};

export default DashboardMain;
